<template>
    <main class="evb-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">
                {{ isEdit ? $t('events.builder.edit_title') : $t('events.builder.create_title') }}
            </div>
        </portal>

        <header class="evb-hero">
            <div>
                <h1 class="evb-hero-title">
                    {{ isEdit ? $t('events.builder.edit_title') : $t('events.builder.create_title') }}
                </h1>
                <p class="evb-hero-sub">{{ $t('events.builder.subtitle') }}</p>
            </div>
            <BmvButton variant="secondary" icon="fal fa-arrow-left" @click="$router.push({ name: 'EventsList' })">
                {{ $t('common.back') }}
            </BmvButton>
        </header>

        <div v-if="pageLoading" class="evb-loader">
            <BmvLoader />
        </div>

        <form v-else class="evb-form" @submit.prevent="save">
            <div class="evb-layout">
                <!-- Colonne principale -->
                <div class="evb-col-main">
                    <div class="evb-card">
                        <h2 class="evb-section-title">{{ $t('events.builder.section_main') }}</h2>

                        <!-- Type -->
                        <div class="evb-field">
                            <label class="evb-label">{{ $t('events.form.type') }} *</label>
                            <nav class="evb-type-toggle">
                                <button
                                    type="button"
                                    class="evb-type-btn"
                                    :class="{ 'is-active': form.type === 'event' }"
                                    @click="form.type = 'event'"
                                >
                                    <i class="fal fa-calendar-alt evb-type-icon"></i>
                                    {{ $t('events.type.event') }}
                                </button>
                                <button
                                    type="button"
                                    class="evb-type-btn"
                                    :class="{ 'is-active': form.type === 'project' }"
                                    @click="form.type = 'project'"
                                >
                                    <i class="fal fa-project-diagram evb-type-icon"></i>
                                    {{ $t('events.type.project') }}
                                </button>
                            </nav>
                        </div>

                        <!-- Title -->
                        <div class="evb-field">
                            <label class="evb-label" for="ev-title">{{ $t('events.form.title') }} *</label>
                            <BmvInput
                                id="ev-title"
                                v-model="form.title"
                                :placeholder="$t('events.form.title_placeholder')"
                                :class="{ 'evb-input--error': fieldErrors.title }"
                                required
                                @input="clearError('title')"
                            />
                            <span v-if="fieldErrors.title" class="evb-error">{{ fieldError('title') }}</span>
                        </div>

                        <!-- Dates -->
                        <div class="evb-row">
                            <div class="evb-field">
                                <label class="evb-label" for="ev-start">{{ $t('events.form.start_date') }}</label>
                                <input
                                    id="ev-start"
                                    v-model="form.start_date"
                                    type="datetime-local"
                                    class="evb-date-input"
                                    :class="{ 'evb-input--error': fieldErrors.start_date }"
                                    @change="clearError('start_date')"
                                />
                                <span v-if="fieldErrors.start_date" class="evb-error">{{ fieldError('start_date') }}</span>
                            </div>
                            <div class="evb-field">
                                <label class="evb-label" for="ev-end">{{ $t('events.form.end_date') }}</label>
                                <input
                                    id="ev-end"
                                    v-model="form.end_date"
                                    type="datetime-local"
                                    class="evb-date-input"
                                    :class="{ 'evb-input--error': fieldErrors.end_date }"
                                    @change="clearError('end_date')"
                                />
                                <span v-if="fieldErrors.end_date" class="evb-error">{{ fieldError('end_date') }}</span>
                            </div>
                        </div>

                        <!-- Description -->
                        <div class="evb-field">
                            <label class="evb-label">{{ $t('events.form.description') }}</label>
                            <ckeditor
                                v-model="form.description"
                                :editor="editor"
                                :config="editorConfig"
                                class="evb-editor"
                            />
                        </div>

                        <!-- Featured -->
                        <div class="evb-field evb-field--inline">
                            <input id="ev-featured" v-model="form.is_featured" type="checkbox" class="evb-checkbox" />
                            <label for="ev-featured" class="evb-label evb-label--inline">
                                {{ $t('events.form.is_featured') }}
                            </label>
                        </div>
                    </div>
                </div>

                <!-- Colonne latérale : couverture + localisation -->
                <div class="evb-col-side">
                    <div class="evb-card">
                        <h2 class="evb-section-title">{{ $t('events.builder.section_cover') }}</h2>
                        <EventCoverUpload
                            :media="form.cover_media"
                            @change="onCoverChange"
                        />
                    </div>
                    <div class="evb-card">
                        <h2 class="evb-section-title">{{ $t('events.builder.section_location') }}</h2>
                        <EventLocationPicker
                            :value="form.location"
                            @input="form.location = $event || { address: '', lat: null, lng: null }"
                        />
                    </div>
                </div>
            </div>

            <div class="evb-footer">
                <BmvButton
                    type="button"
                    variant="secondary"
                    @click="$router.push({ name: 'EventsList' })"
                >
                    {{ $t('common.cancel') }}
                </BmvButton>
                <BmvButton
                    type="submit"
                    variant="primary"
                    icon="fal fa-check"
                    :loading="saving"
                >
                    {{ $t('common.save') }}
                </BmvButton>
            </div>
        </form>
    </main>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import { mapActions, mapState } from 'vuex'
import BmvButton from '@/components/UI/v2/BmvButton.vue'
import BmvInput from '@/components/UI/v2/BmvInput.vue'
import BmvLoader from '@/components/UI/v2/BmvLoader.vue'
import EventLocationPicker from '@/components/Events/EventLocationPicker.vue'
import EventCoverUpload from '@/components/Events/EventCoverUpload.vue'

function emptyForm() {
    return {
        type: 'event',
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        is_featured: false,
        cover_media: null,
        location: { address: '', lat: null, lng: null }
    }
}

function isoToLocal(iso) {
    if (!iso) return ''
    const d = new Date(iso)
    if (isNaN(d)) return ''
    const pad = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export default {
    name: 'EventBuilder',
    components: { BmvButton, BmvInput, BmvLoader, EventLocationPicker, EventCoverUpload },
    data() {
        return {
            pageLoading: false,
            saving: false,
            form: emptyForm(),
            fieldErrors: {},
            editor: ClassicEditor,
            editorConfig: {
                toolbar: {
                    items: [
                        'heading', '|',
                        'bold', 'italic', 'link', '|',
                        'bulletedList', 'numberedList', '|',
                        'blockQuote', '|',
                        'undo', 'redo'
                    ]
                }
            }
        }
    },
    computed: {
        ...mapState('events', { event: (state) => state.event }),
        isEdit() {
            return !!this.$route.params.id
        }
    },
    async created() {
        if (this.isEdit) {
            this.pageLoading = true
            try {
                await this.getEvent(this.$route.params.id)
                this.fillForm(this.event)
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
            } finally {
                this.pageLoading = false
            }
        }
    },
    methods: {
        ...mapActions('events', ['getEvent', 'createEvent', 'updateEvent']),
        fillForm(ev) {
            if (!ev) return
            this.form = {
                type: ev.type || 'event',
                title: ev.title || '',
                description: ev.description || '',
                start_date: isoToLocal(ev.start_date),
                end_date: isoToLocal(ev.end_date),
                is_featured: !!ev.is_featured,
                cover_media: ev.cover_media || null,
                location: ev.location
                    ? { address: ev.location.address || '', lat: ev.location.lat || null, lng: ev.location.lng || null }
                    : { address: '', lat: null, lng: null }
            }
        },
        buildPayload() {
            const p = {
                type: this.form.type,
                title: this.form.title,
                description: this.form.description || null,
                is_featured: this.form.is_featured,
                start_date: this.form.start_date || null,
                end_date: this.form.end_date || null
            }
            if (this.form.location.address || this.form.location.lat || this.form.location.lng) {
                p.location = {
                    address: this.form.location.address || null,
                    lat: this.form.location.lat || null,
                    lng: this.form.location.lng || null
                }
            } else {
                p.location = null
            }
            if (this.form.cover_media && this.form.cover_media.base64) {
                p.cover_media = {
                    name: this.form.cover_media.name,
                    encodedFile: this.form.cover_media.base64
                }
            } else if (this.form.cover_media === null && this.isEdit) {
                p.cover_media = null
            }
            return p
        },
        onCoverChange(media) {
            this.$set(this.form, 'cover_media', media || null)
        },
        fieldError(key) {
            const errs = this.fieldErrors[key]
            if (!errs || !errs.length) return ''
            const raw = errs[0]
            // Backend sends "validation.rule" — our i18n nests these under "error.validation.rule"
            if (raw.startsWith('validation.')) {
                const mapped = 'error.' + raw
                if (this.$te(mapped)) return this.$t(mapped)
            }
            return this.$te(raw) ? this.$t(raw) : raw
        },
        clearError(key) {
            if (this.fieldErrors[key]) {
                this.$delete(this.fieldErrors, key)
            }
        },
        async save() {
            if (this.saving) return
            this.fieldErrors = {}
            this.saving = true
            try {
                if (this.isEdit) {
                    await this.updateEvent({ id: this.$route.params.id, payload: this.buildPayload() })
                } else {
                    await this.createEvent(this.buildPayload())
                }
                this.$store.dispatch('toast', { type: 'success', title: this.$t('common.save') })
                this.$router.push({ name: 'EventsList' })
            } catch (err) {
                if (err.response && err.response.data && err.response.data.errors) {
                    this.fieldErrors = err.response.data.errors
                } else {
                    this.$store.dispatch('dispatchError', err)
                }
            } finally {
                this.saving = false
            }
        }
    }
}
</script>

<style scoped>
.evb-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.evb-hero {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: var(--v2-space-6);
}

.evb-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.evb-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
}

.evb-loader {
    display: flex;
    justify-content: center;
    padding: 80px 0;
}

.evb-form {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-5);
}

.evb-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--v2-space-5);
    align-items: start;
}

.evb-col-main,
.evb-col-side {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-5);
    min-width: 0;
}

.evb-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.evb-section-title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--v2-text-1);
    margin: 0 0 4px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--v2-border);
}

.evb-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.evb-field--inline {
    flex-direction: row;
    align-items: center;
    gap: 10px;
}

.evb-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.evb-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-text-2);
}

.evb-label--inline {
    margin: 0;
    cursor: pointer;
}

.evb-date-input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-surface-1);
    color: var(--v2-text-1);
    font-size: 0.875rem;
    outline: none;
    transition: border-color 0.15s;
    font-family: var(--v2-font-sans);
    box-sizing: border-box;
}

.evb-date-input.evb-input--error,
:deep(.evb-input--error input),
:deep(.evb-input--error .bmv-input) {
    border-color: #ef4444 !important;
}

.evb-error {
    font-size: 0.78rem;
    color: #ef4444;
    margin-top: 2px;
}

.evb-date-input:focus {
    border-color: var(--v2-primary);
}

.evb-checkbox {
    width: 16px;
    height: 16px;
    accent-color: var(--v2-primary);
    cursor: pointer;
    flex-shrink: 0;
}
.evb-type-toggle {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    background: #f1f5f9;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 4px;
    align-self: flex-start;
}

.evb-type-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: 8px;
    font-size: 0.78rem;
    font-weight: 500;
    color: #64748b;
    background: none;
    border: none;
    cursor: pointer;
    font-family: var(--v2-font-sans);
    transition: all 0.15s ease;
    white-space: nowrap;
}

.evb-type-btn:hover {
    color: #0891B2;
    background: rgba(8, 145, 178, 0.06);
}

.evb-type-btn.is-active {
    background: #fff;
    color: #0891B2;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.evb-type-icon {
    font-size: 0.78rem;
    color: #94a3b8;
    flex-shrink: 0;
}

.evb-type-btn:hover .evb-type-icon,
.evb-type-btn.is-active .evb-type-icon {
    color: #0891B2;
}

.evb-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

@media (max-width: 1024px) {
    .evb-layout {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 640px) {
    .evb-v2 {
        padding: 16px 18px 32px;
    }

    .evb-hero {
        flex-direction: column;
    }

    .evb-row {
        grid-template-columns: 1fr;
    }
}
</style>
